import { useState } from "react";
import { Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";

const REASONS = [
  { value: "scope", label: "Not relevant" },
  { value: "methodology", label: "Methodology" },
  { value: "superseded", label: "Superseded" },
  { value: "duplicate", label: "Duplicate" },
  { value: "not_found", label: "Not found" },
  { value: "other", label: "Other" },
] as const;

interface Props {
  onDismiss: (reason: string, reasonText?: string, findSimilar?: boolean) => void;
  findSimilarDisabled?: boolean;
  /** Text button ("Dismiss…") instead of the bare icon. */
  labelled?: boolean;
}

export function DismissPopover({ onDismiss, findSimilarDisabled, labelled }: Props) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>("scope");
  const [reasonText, setReasonText] = useState("");
  const [findSimilar, setFindSimilar] = useState(false);

  const handleConfirm = () => {
    if (!selected) return;
    onDismiss(selected, reasonText || undefined, findSimilar);
    setOpen(false);
    setSelected("scope");
    setReasonText("");
    setFindSimilar(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {labelled ? (
          <Button variant="outline" size="xs" data-guide-target="dismissSimilarButton">
            <Ban /> Dismiss…
          </Button>
        ) : (
          <Button variant="ghost" size="icon-sm" title="Dismiss" data-guide-target="dismissSimilarButton">
            <Ban className="size-3.5" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <div className="flex flex-col gap-1">
          {REASONS.map((r) => (
            <button
              key={r.value}
              onClick={() => setSelected(r.value)}
              className={`rounded px-2 py-1 text-left text-sm transition-colors ${
                selected === r.value
                  ? "bg-accent text-accent-foreground"
                  : "hover:bg-muted"
              }`}
            >
              {r.label}
            </button>
          ))}
          {selected && (
            <>
              <input
                type="text"
                placeholder="e.g. wrong population, studies adults"
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
                className="mt-1 rounded border bg-background px-2 py-1 text-sm"
                autoFocus={false}
              />
              <label className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                <Switch
                  checked={findSimilar}
                  onCheckedChange={setFindSimilar}
                  disabled={findSimilarDisabled}
                  aria-describedby={findSimilarDisabled ? "dismiss-similar-why" : undefined}
                  className="scale-75"
                />
                Find & dismiss similar
              </label>
              {findSimilarDisabled && (
                <p id="dismiss-similar-why" className="text-[11px] text-muted-foreground">Finish the open similar-papers review first.</p>
              )}
            </>
          )}
          <Button
            size="sm"
            className="mt-1"
            disabled={!selected}
            onClick={handleConfirm}
          >
            {findSimilar ? "Dismiss & Find Similar" : "Confirm"}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
